/**
 * Azure Functions API Client
 * Handles all HTTP requests to the BRR backend API (Neo4j + Box)
 */

import {
  ApiConfig,
  BRRData,
  BatchSummary,
  Batch,
  Document,
  DocumentUploadResponse,
  InspectionLotInfoResponse,
  BRRProcessRequest,
  BRRProcessResponse,
  OrchestratorStatus,
  ValidationPageData,
  BRRFormDataResponse,
  BRRReviewPayload,
  BRRReviewSaveResponse,
  BRRReviewComplete,
  StartPageData,
  DocumentValidationData,
  BRRFormData,
  QPReviewData,
  TraceLinkLotStatusResponse,
  TraceLinkHierarchyLevelsResponse,
  FieldHighlightRect,
  QAACheckResponse,
  BoxFileEntry,
  BoxLinkEntry,
  BoxFolderScanResult,
  BoxFolderInfo,
  BoxSubsectionKey,
  LotReleaseDatesResponse,
  BRRLevel,
  EnrichedProduct,
  PackagedProductsResponse,
  ConnectionStatusResponse,
  BRRReviewsByBatchResponse,
  BRRReviewsByMaterialResponse,
  BRRCloneRequest,
  BRRCloneResponse,
  BRRConflictCheckResponse,
  BRRFormDataChangeControl,
} from './types';
import { authFetch } from './authFetch';
import { getApiToken } from './apiToken';
import { acquireIdToken } from '../auth/msalToken';

/**
 * Creates an API client instance with the given configuration
 */
export const createBRRApi = (config: ApiConfig) => {
  const { baseUrl } = config;

  // Helper to create headers (the bearer and X-API-TOKEN are added by authFetch).
  // A plain record rather than HeadersInit: authFetch merges these by spreading,
  // so a Headers instance here would drop them.
  const getHeaders = (includeContentType = true): Record<string, string> => {
    const headers: Record<string, string> = {};
    if (includeContentType) {
      headers['Content-Type'] = 'application/json';
    }
    return headers;
  };

  // Helper for error handling
  const handleResponse = async <T>(response: Response): Promise<T> => {
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`API Error (${response.status}): ${errorText || response.statusText}`);
    }
    return response.json();
  };

  // Backend response type (snake_case from API)
  interface BRRReviewBackendResponse {
    batch_id: number;
    session_id: string;
    user_email: string;
    batch_type?: 'direct' | 'indirect';
    priority?: 'low' | 'medium' | 'high';
    priority_locked_by?: string;
    priority_locked_at?: string;
    status: 'draft' | 'submitted';
    saved_at?: string;
    cloned_from_session_id?: string;
    cloned_at?: string;
    start_page?: Record<string, unknown>;
    document_validation?: Record<string, unknown>;
    brr_form?: Record<string, unknown>;
    qp_review?: Record<string, unknown>;
  }

  // Convert snake_case string to camelCase, handling known abbreviations
  const snakeToCamel = (str: string): string => {
    const converted = str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
    // Handle known abbreviations that should remain uppercase.
    // Must handle both end-of-string (e.g., manuallyAddedCcs → manuallyAddedCCs)
    // and mid-string cases (e.g., removedCcIds → removedCCIds).
    return converted
      .replace(/Ccs$/g, 'CCs')     // Change Controls at end (e.g., manuallyAddedCcs)
      .replace(/Qes$/g, 'QEs')     // Quality Events at end (e.g., manuallyAddedQes)
      .replace(/CcIds$/g, 'CCIds') // Change Control IDs (e.g., removedCcIds)
      .replace(/QeIds$/g, 'QEIds'); // Quality Event IDs (e.g., removedQeIds)
  };

  // Fields whose values contain data keys (extraction keys, field IDs) that should NOT be transformed.
  // These are data-carrying objects, not API response structures.
  const DATA_FIELDS = new Set([
    'overrides',           // DocumentValidation: extraction keys like 'product_name', 'destination_country'
    'synced_results',      // DocumentValidation: synced validation results
    'syncedResults',       // camelCase alias
    'manual_sections',     // DocumentValidation: manual section entries
    'manualSections',      // camelCase alias
    'field_overrides',     // FormPage/ConfirmationPage: field override values
    'fieldOverrides',      // camelCase alias
    'compliance_overrides', // FormPage/ConfirmationPage: compliance decisions
    'complianceOverrides', // camelCase alias
    'auto_populated_data', // FormPage/ConfirmationPage: full form snapshot
    'autoPopulatedData',   // camelCase alias
    'manually_added_ccs',  // FormPage: manually added change control cards
    'manuallyAddedCCs',    // camelCase alias
    'manually_added_qes',  // FormPage: manually added quality event cards
    'manuallyAddedQEs',    // camelCase alias
    'document_section_data', // FormPage: technical doc sections keyed by BoxSubsectionKey
    'documentSectionData',   // camelCase alias
    'custom_document_sections', // FormPage: user-added custom document sections
    'customDocumentSections',   // camelCase alias
    'individual_cc_notes',     // FormPage: per-CC validation notes keyed by stableId
    'individualCCNotes',       // camelCase alias
  ]);

  // Transform snake_case keys to camelCase recursively, but preserve data keys
  const transformKeys = (obj: Record<string, unknown>, preserveKeys = false): Record<string, unknown> => {
    if (!obj || typeof obj !== 'object') return obj;
    if (Array.isArray(obj)) return obj.map(item =>
      typeof item === 'object' && item !== null ? transformKeys(item as Record<string, unknown>, preserveKeys) : item
    ) as unknown as Record<string, unknown>;

    const transformed: Record<string, unknown> = {};
    for (const key in obj) {
      // Use Object.prototype.hasOwnProperty.call for backward compatibility (pre-ES2022)
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        // If preserveKeys is true, keep the original key; otherwise transform to camelCase
        const outputKey = preserveKeys ? key : snakeToCamel(key);
        const value = obj[key];

        // Once preserveKeys is true, keep it true for all nested levels (sticky).
        // Otherwise, check if THIS field's value should have its keys preserved.
        const shouldPreserveNestedKeys = preserveKeys || DATA_FIELDS.has(key) || DATA_FIELDS.has(outputKey);

        transformed[outputKey] = typeof value === 'object' && value !== null && !Array.isArray(value)
          ? transformKeys(value as Record<string, unknown>, shouldPreserveNestedKeys)
          : value;
      }
    }
    return transformed;
  };

  // Transform snake_case keys to camelCase for BRR review responses
  const transformReviewResponse = (data: BRRReviewBackendResponse | null): BRRReviewComplete | null => {
    if (!data) return null;

    return {
      batchId: data.batch_id,
      sessionId: data.session_id,
      userEmail: data.user_email,
      batchType: data.batch_type,
      priority: data.priority,
      priorityLockedBy: data.priority_locked_by,
      priorityLockedAt: data.priority_locked_at,
      status: data.status,
      savedAt: data.saved_at,
      clonedFromSessionId: data.cloned_from_session_id,
      clonedAt: data.cloned_at,
      // Transform page section keys from snake_case to camelCase (including nested keys)
      startPage: data.start_page ? transformKeys(data.start_page) as unknown as StartPageData : undefined,
      documentValidation: data.document_validation ? transformKeys(data.document_validation) as unknown as DocumentValidationData : undefined,
      brrForm: data.brr_form ? transformKeys(data.brr_form) as unknown as BRRFormData : undefined,
      qpReview: data.qp_review ? transformKeys(data.qp_review) as unknown as QPReviewData : undefined,
    };
  };

  // In-memory cache for getSavedReview — deduplicates concurrent and rapid successive calls
  const reviewCache = new Map<string, { promise: Promise<BRRReviewComplete | null>; expiresAt: number }>();
  const REVIEW_CACHE_TTL_MS = 10_000;

  return {
    /**
     * Get batch summary for dashboard display
     */
    async getBatchSummary(batchId: string): Promise<BatchSummary> {
      const response = await authFetch(`${baseUrl}/api/brr/${batchId}/summary`, {
        method: 'GET',
        headers: getHeaders(false)
      });
      return handleResponse<BatchSummary>(response);
    },

    /**
     * Get full batch data including BRR form data
     */
    async getBatch(batchId: string): Promise<Batch> {
      const response = await authFetch(`${baseUrl}/api/brr/${batchId}`, {
        method: 'GET',
        headers: getHeaders(false)
      });
      return handleResponse<Batch>(response);
    },

    /**
     * Get full BRR data for editing
     */
    async getBRRData(batchId: string): Promise<BRRData> {
      const response = await authFetch(`${baseUrl}/api/brr/${batchId}`, {
        method: 'GET',
        headers: getHeaders(false)
      });
      return handleResponse<BRRData>(response);
    },

    /**
     * Populate/initialize a new BRR from batch data
     * Extracts data from Neo4j and prepares form
     */
    async populateBRR(batchId: string): Promise<BRRData> {
      const response = await authFetch(`${baseUrl}/api/brr/${batchId}/populate`, {
        method: 'POST',
        headers: getHeaders()
      });
      return handleResponse<BRRData>(response);
    },

    /**
     * Save BRR draft (auto-save)
     * Stores in Neo4j with status = 'draft'
     */
    async saveDraft(batchId: string, data: BRRData): Promise<void> {
      const response = await authFetch(`${baseUrl}/api/brr/${batchId}/draft`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify({
          ...data,
          status: 'draft',
          lastModifiedAt: new Date().toISOString()
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to save draft: ${errorText || response.statusText}`);
      }
    },

    /**
     * Submit BRR for QP review
     * Updates status in Neo4j and triggers the backend workflow
     */
    async submitBRR(
      batchId: string,
      data: BRRData,
      assignedQP: string
    ): Promise<void> {
      const response = await authFetch(`${baseUrl}/api/brr/${batchId}/submit`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          ...data,
          status: 'submitted',
          submittedBy: data.lastModifiedBy,
          submittedAt: new Date().toISOString(),
          assignedQP
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to submit BRR: ${errorText || response.statusText}`);
      }
    },

    /**
     * Get documents for a batch
     */
    async getDocuments(batchId: string): Promise<Document[]> {
      const response = await authFetch(`${baseUrl}/api/brr/${batchId}/documents`, {
        method: 'GET',
        headers: getHeaders(false)
      });
      return handleResponse<Document[]>(response);
    },

    /**
     * Upload documents to blob storage
     * @param sessionId - Session ID for isolation (documents stored at user_name/session_id/)
     *
     * Stays on XMLHttpRequest for its upload progress events, so the bearer
     * token is acquired here rather than through authFetch.
     */
    async uploadDocuments(
      files: File[],
      onProgress?: (fileIndex: number, progress: number) => void,
      sessionId?: string | null
    ): Promise<DocumentUploadResponse> {
      const token = await acquireIdToken();

      return new Promise((resolve, reject) => {
        const formData = new FormData();
        if (sessionId) {
          formData.append('session_id', sessionId);
        }

        // Append all files
        files.forEach((file) => {
          formData.append('file', file);
        });

        const xhr = new XMLHttpRequest();

        // Track upload progress
        if (onProgress) {
          xhr.upload.addEventListener('progress', (e) => {
            if (e.lengthComputable) {
              const percentComplete = (e.loaded / e.total) * 100;
              // Report progress for all files (we don't track individual file progress in this API)
              onProgress(0, percentComplete);
            }
          });
        }

        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              const response = JSON.parse(xhr.responseText);
              resolve(response);
            } catch (error) {
              reject(new Error('Invalid JSON response from server'));
            }
          } else {
            try {
              const errorResponse = JSON.parse(xhr.responseText);
              reject(new Error(errorResponse.error || `Upload failed: ${xhr.statusText}`));
            } catch {
              reject(new Error(`Upload failed: ${xhr.statusText}`));
            }
          }
        });

        xhr.addEventListener('error', () => {
          reject(new Error('Network error during upload'));
        });

        xhr.addEventListener('abort', () => {
          reject(new Error('Upload cancelled'));
        });

        xhr.open('POST', `${baseUrl}/api/documents/upload`);
        xhr.setRequestHeader('X-API-TOKEN', getApiToken());

        if (token) {
          xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        }

        xhr.send(formData);
      });
    },

    /**
     * Delete a document
     */
    async deleteDocument(batchId: string, documentId: string): Promise<void> {
      const response = await authFetch(
        `${baseUrl}/api/brr/${batchId}/documents/${documentId}`,
        {
          method: 'DELETE',
          headers: getHeaders(false)
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to delete document: ${errorText || response.statusText}`);
      }
    },

    /**
     * Search batches (for dashboard)
     */
    async searchBatches(query: {
      searchTerm?: string;
      status?: string;
      dateFrom?: string;
      dateTo?: string;
      limit?: number;
      offset?: number;
    }): Promise<{ batches: Batch[]; total: number }> {
      const params = new URLSearchParams();
      if (query.searchTerm) params.append('q', query.searchTerm);
      if (query.status) params.append('status', query.status);
      if (query.dateFrom) params.append('dateFrom', query.dateFrom);
      if (query.dateTo) params.append('dateTo', query.dateTo);
      if (query.limit) params.append('limit', query.limit.toString());
      if (query.offset) params.append('offset', query.offset.toString());

      const response = await authFetch(
        `${baseUrl}/api/brr/search?${params.toString()}`,
        {
          method: 'GET',
          headers: getHeaders(false)
        }
      );

      return handleResponse<{ batches: Batch[]; total: number }>(response);
    },

    /**
     * Get user's recent batches
     */
    async getUserBatches(userEmail: string, limit = 10): Promise<Batch[]> {
      const response = await authFetch(
        `${baseUrl}/api/brr/user/${encodeURIComponent(userEmail)}/batches?limit=${limit}`,
        {
          method: 'GET',
          headers: getHeaders(false)
        }
      );
      return handleResponse<Batch[]>(response);
    },

    /**
     * Get batch, product, and country info for an inspection lot
     */
    async getInspectionLotInfo(inspectionLotId: string, brrLevel: BRRLevel = 'na'): Promise<InspectionLotInfoResponse> {
      const response = await authFetch(`${baseUrl}/api/inspection-lot/info`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ inspectionLotId, brrLevel })
      });
      return handleResponse<InspectionLotInfoResponse>(response);
    },

    async getPackagedProducts(pharmaceuticalProductName: string): Promise<PackagedProductsResponse> {
      const response = await authFetch(`${baseUrl}/api/pharmaceutical-product/packaged-products`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ pharmaceuticalProductName })
      });
      return handleResponse<PackagedProductsResponse>(response);
    },

    /**
     * Search inspection lots by query prefix
     */
    async searchInspectionLots(
      query: string,
      limit?: number
    ): Promise<{ results: { lotId: string }[]; count: number; query: string }> {
      const params = new URLSearchParams();
      params.append('q', query.trim());
      if (limit != null && limit > 0) {
        params.append('limit', limit.toString());
      }

      const response = await authFetch(
        `${baseUrl}/api/inspection-lot/search?${params.toString()}`,
        {
          method: 'GET',
          headers: getHeaders(false)
        }
      );

      return handleResponse<{ results: { lotId: string }[]; count: number; query: string }>(response);
    },

    /**
     * Search batch IDs by query prefix or substring.
     * @param field - Which field to search: batch_id (default), material_id, or vendor_batch_id
     */
    async searchBatchIds(
      query: string,
      limit?: number,
      field: 'batch_id' | 'material_id' | 'vendor_batch_id' = 'batch_id'
    ): Promise<{ results: { batchId: string }[]; count: number; query: string }> {
      const params = new URLSearchParams();
      params.append('q', query.trim());
      if (limit != null && limit > 0) {
        params.append('limit', limit.toString());
      }
      if (field !== 'batch_id') {
        params.append('field', field);
      }

      const response = await authFetch(
        `${baseUrl}/api/batch/search?${params.toString()}`,
        {
          method: 'GET',
          headers: getHeaders(false)
        }
      );

      return handleResponse<{ results: { batchId: string }[]; count: number; query: string }>(response);
    },

    /**
     * Search materials by material_id prefix.
     */
    async searchMaterials(
      query: string,
      limit?: number
    ): Promise<{ results: { materialId: string; productName: string | null }[]; count: number; query: string }> {
      const params = new URLSearchParams();
      params.append('q', query.trim());
      if (limit != null && limit > 0) {
        params.append('limit', limit.toString());
      }

      const response = await authFetch(
        `${baseUrl}/api/material/search?${params.toString()}`,
        {
          method: 'GET',
          headers: getHeaders(false)
        }
      );

      return handleResponse<{ results: { materialId: string; productName: string | null }[]; count: number; query: string }>(response);
    },

    /**
     * Search vendor batch IDs by substring.
     */
    async searchVendorBatches(
      query: string,
      limit?: number
    ): Promise<{ results: { vendorBatchId: string }[]; count: number; query: string }> {
      const params = new URLSearchParams();
      params.append('q', query.trim());
      if (limit != null && limit > 0) {
        params.append('limit', limit.toString());
      }

      const response = await authFetch(
        `${baseUrl}/api/vendor-batch/search?${params.toString()}`,
        {
          method: 'GET',
          headers: getHeaders(false)
        }
      );

      return handleResponse<{ results: { vendorBatchId: string }[]; count: number; query: string }>(response);
    },

    /**
     * Search CARA change controls by name/number
     */
    async searchChangeControls(
      query: string,
      limit = 10
    ): Promise<{ results: BRRFormDataChangeControl[]; count: number; query: string }> {
      const params = new URLSearchParams();
      params.append('q', query.trim());
      params.append('limit', limit.toString());

      const response = await authFetch(
        `${baseUrl}/api/change-controls/search?${params.toString()}`,
        {
          method: 'GET',
          headers: getHeaders(false)
        }
      );

      return handleResponse<{ results: BRRFormDataChangeControl[]; count: number; query: string }>(response);
    },

    /**
     * Search CARA products (PackagedMedicinalProduct) by name
     */
    async searchProducts(
      query: string,
      limit = 20,
      offset = 0
    ): Promise<{ results: EnrichedProduct[]; count: number; query: string; hasMore: boolean }> {
      const params = new URLSearchParams();
      params.append('q', query.trim());
      params.append('limit', limit.toString());
      if (offset > 0) {
        params.append('offset', offset.toString());
      }

      const response = await authFetch(
        `${baseUrl}/api/products/search?${params.toString()}`,
        {
          method: 'GET',
          headers: getHeaders(false)
        }
      );

      return handleResponse<{
        results: EnrichedProduct[];
        count: number;
        query: string;
        hasMore: boolean;
      }>(response);
    },

    /**
     * Record a user's product selection in the graph.
     * Creates/updates a MATCHED_TO relationship with audit metadata.
     */
    async confirmProductMatch(
      batchId: number,
      productUid: string,
      confirmedBy: string
    ): Promise<{ success: boolean }> {
      const response = await authFetch(
        `${baseUrl}/api/products/confirm-match`,
        {
          method: 'POST',
          headers: getHeaders(true),
          body: JSON.stringify({ batchId, productUid, confirmedBy }),
        }
      );

      return handleResponse<{ success: boolean }>(response);
    },

    /**
     * Get inspection lots for a specific batch ID
     */
    async getInspectionLotsByBatch(
      batchId: string,
      limit?: number
    ): Promise<{ results: { lotId: string }[]; count: number; batch_id: string; message?: string }> {
      const params = new URLSearchParams();
      params.append('batch_id', batchId.trim());
      if (limit != null && limit > 0) {
        params.append('limit', limit.toString());
      }

      const response = await authFetch(
        `${baseUrl}/api/batch/inspection-lots?${params.toString()}`,
        {
          method: 'GET',
          headers: getHeaders(false)
        }
      );

      return handleResponse<{ results: { lotId: string }[]; count: number; batch_id: string; message?: string }>(response);
    },

    /**
     * Get SAP release dates for a batch of inspection lot IDs.
     * Used by the dashboard to compute BRR-to-release delay.
     */
    async getLotReleaseDates(lotIds: string[]): Promise<LotReleaseDatesResponse> {
      const response = await authFetch(
        `${baseUrl}/api/lots/release-dates`,
        {
          method: 'POST',
          headers: getHeaders(),
          body: JSON.stringify({ lotIds })
        }
      );
      return handleResponse<LotReleaseDatesResponse>(response);
    },

    /**
     * Get validation table data from extraction results
     */
    async getValidationData(sessionId?: string | null): Promise<ValidationPageData | null> {
      let url = `${baseUrl}/api/brr/validation-data`;
      if (sessionId) {
        url += `?session_id=${encodeURIComponent(sessionId)}`;
      }
      const response = await authFetch(url, {
        method: 'GET',
        headers: getHeaders(false)
      });
      if (response.status === 404) {
        return null;
      }
      return handleResponse<ValidationPageData>(response);
    },

    /**
     * Save user overrides to the most recent extraction results
     */
    async saveValidationOverrides(
      sessionId: string,
      overrides: Record<string, Record<string, string>>
    ): Promise<void> {
      const response = await authFetch(`${baseUrl}/api/brr/validation-data`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify({ session_id: sessionId, overrides })
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to save overrides: ${errorText || response.statusText}`);
      }
    },

    /**
     * Upload a single file into a Box folder owned by the given inspection lot.
     * Used for additional-evidence PDFs; a name collision is disambiguated server-side.
     *
     * Stays on XMLHttpRequest for its upload progress events, so the bearer
     * token is acquired here rather than through authFetch.
     */
    async uploadFileToBox(
      folderId: string,
      inspectionLotId: string,
      file: File,
      onProgress?: (progress: number) => void
    ): Promise<{ success: boolean; file_id: string; file_name: string }> {
      const token = await acquireIdToken();

      return new Promise((resolve, reject) => {
        const formData = new FormData();
        formData.append('folder_id', folderId);
        formData.append('inspection_lot_id', inspectionLotId);
        formData.append('file', file);

        const xhr = new XMLHttpRequest();

        if (onProgress) {
          xhr.upload.addEventListener('progress', (e) => {
            if (e.lengthComputable) {
              onProgress((e.loaded / e.total) * 100);
            }
          });
        }

        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              resolve(JSON.parse(xhr.responseText));
            } catch (error) {
              reject(new Error('Invalid JSON response from server'));
            }
          } else {
            try {
              const errorResponse = JSON.parse(xhr.responseText);
              reject(new Error(errorResponse.error || `Box upload failed: ${xhr.statusText}`));
            } catch {
              reject(new Error(`Box upload failed: ${xhr.statusText}`));
            }
          }
        });

        xhr.addEventListener('error', () => {
          reject(new Error('Network error during upload'));
        });

        xhr.addEventListener('abort', () => {
          reject(new Error('Upload cancelled'));
        });

        xhr.open('POST', `${baseUrl}/api/box/upload-file`);
        xhr.setRequestHeader('X-API-TOKEN', getApiToken());

        if (token) {
          xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        }

        xhr.send(formData);
      });
    },

    /**
     * Delete a document from blob storage
     */
    async deleteBlobDocument(blobName: string, container?: string): Promise<void> {
      const params = new URLSearchParams();
      params.append('blob_name', blobName);
      if (container) {
        params.append('container', container);
      }

      const response = await authFetch(
        `${baseUrl}/api/documents/delete?${params.toString()}`,
        {
          method: 'DELETE',
          headers: getHeaders(false)
        }
      );

      // 404 means the blob was already deleted (e.g. by the orchestrator after extraction)
      if (!response.ok && response.status !== 404) {
        const errorText = await response.text();
        throw new Error(`Failed to delete document: ${errorText || response.statusText}`);
      }
    },

    /**
     * Delete all of the calling user's documents from blob storage
     * @param sessionId - If provided, only deletes documents for that session
     */
    async clearUserDocuments(sessionId?: string | null): Promise<{ deleted_count: number; errors: Array<{ blob_name: string; error: string }> }> {
      const params = new URLSearchParams();
      if (sessionId) {
        params.append('session_id', sessionId);
      }

      const response = await authFetch(
        `${baseUrl}/api/documents/clear?${params.toString()}`,
        {
          method: 'DELETE',
          headers: getHeaders(false)
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to clear documents: ${errorText || response.statusText}`);
      }

      return response.json();
    },

    /**
     * Start BRR document processing orchestrator
     */
    async startBRRProcess(request: BRRProcessRequest): Promise<BRRProcessResponse> {
      const response = await authFetch(`${baseUrl}/api/brr/process`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(request)
      });
      return handleResponse<BRRProcessResponse>(response);
    },

    /**
     * Get orchestrator status by polling the status URL
     */
    async getOrchestratorStatus(instanceId: string): Promise<OrchestratorStatus> {
      // Call our own backend proxy instead of the Durable webhook endpoint directly.
      // The webhook endpoint (/runtime/webhooks/durabletask/) requires an internal
      // system key and 403s from browsers on Flex Consumption plans even with a valid key.
      const response = await authFetch(`${baseUrl}/api/brr/status/${instanceId}`, {
        method: 'GET',
        headers: getHeaders(false)
      });
      return handleResponse<OrchestratorStatus>(response);
    },

    async terminateOrchestrator(instanceId: string): Promise<{ terminated: boolean }> {
      const response = await authFetch(`${baseUrl}/api/brr/terminate/${instanceId}`, {
        method: 'POST',
        headers: getHeaders(false)
      });
      return handleResponse<{ terminated: boolean }>(response);
    },

    async cleanupBlobs(blobPrefix: string): Promise<{ cleaned: boolean; deleted_count: number }> {
      const response = await authFetch(`${baseUrl}/api/brr/cleanup`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ blob_prefix: blobPrefix })
      });
      return handleResponse<{ cleaned: boolean; deleted_count: number }>(response);
    },

    /**
     * Get BRR form auto-population data from the graph
     * @param sessionId - Optional BRR session ID for session-scoped document filtering
     * @param selectedLotIds - Optional array of all selected lot IDs for multi-lot BRRs
     */
    async getFormData(
      inspectionLotId: string,
      productName?: string,
      sessionId?: string,
      selectedLotIds?: string[]
    ): Promise<BRRFormDataResponse> {
      const body: Record<string, string | string[]> = { inspectionLotId: inspectionLotId.trim() };
      if (productName) {
        body.productName = productName.trim();
      }
      if (sessionId) {
        body.sessionId = sessionId;
      }
      if (selectedLotIds && selectedLotIds.length > 0) {
        body.selectedLotIds = selectedLotIds;
      }
      const response = await authFetch(`${baseUrl}/api/brr/form-data`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(body)
      });
      return handleResponse<BRRFormDataResponse>(response);
    },

    /**
     * Write validated extractions to the Neo4j graph.
     * Called after the user confirms all fields are reviewed.
     * @param sessionId - Optional BRR session ID for session-scoped document isolation
     */
    async writeToGraph(
      blobPrefix: string,
      batchId: string,
      productName?: string,
      sessionId?: string
    ): Promise<{ success: boolean; documents_written: number; document_types: string[] }> {
      const body: Record<string, string> = { blobPrefix, batchId };
      if (productName) {
        body.productName = productName;
      }
      if (sessionId) {
        body.sessionId = sessionId;
      }
      const response = await authFetch(`${baseUrl}/api/documents/write-to-graph`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(body)
      });
      return handleResponse<{ success: boolean; documents_written: number; document_types: string[] }>(response);
    },

    /**
     * Save a BRR review snapshot to the Neo4j graph.
     * Merges a BRRReview node keyed on (batch_id, session_id).
     */
    async saveReview(payload: BRRReviewPayload): Promise<BRRReviewSaveResponse> {
      const response = await authFetch(`${baseUrl}/api/brr/review`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(payload)
      });
      const result = await handleResponse<BRRReviewSaveResponse>(response);
      reviewCache.delete(`${payload.batchId}:${payload.sessionId}`);
      return result;
    },

    /**
     * Retrieve a saved BRR review from the Neo4j graph.
     * Returns the complete review data with nested page sections, or null if not found.
     */
    async getSavedReview(batchId: string, sessionId: string): Promise<BRRReviewComplete | null> {
      const cacheKey = `${batchId}:${sessionId}`;
      const cached = reviewCache.get(cacheKey);
      if (cached && Date.now() < cached.expiresAt) {
        return cached.promise;
      }

      const promise = (async () => {
        const params = new URLSearchParams();
        params.append('batchId', batchId);
        params.append('sessionId', sessionId);

        const response = await authFetch(`${baseUrl}/api/brr/review?${params.toString()}`, {
          method: 'GET',
          headers: getHeaders(false),
        });

        if (response.status === 404) {
          return null;
        }

        const rawData = await handleResponse<any>(response);
        return transformReviewResponse(rawData);
      })();

      reviewCache.set(cacheKey, { promise, expiresAt: Date.now() + REVIEW_CACHE_TTL_MS });
      promise.catch(() => { reviewCache.delete(cacheKey); });

      return promise;
    },

    /**
     * Check for existing BRR conflicts for a batch + lot combination.
     * Detects exact matches (same batch + lots) and partial matches (overlapping lots).
     */
    async checkConflict(
      batchId: string,
      lots: string[],
      excludeSessionId?: string
    ): Promise<BRRConflictCheckResponse> {
      const params = new URLSearchParams();
      params.append('batchId', batchId);
      params.append('lots', lots.join(','));
      if (excludeSessionId) {
        params.append('excludeSessionId', excludeSessionId);
      }

      const response = await authFetch(`${baseUrl}/api/brr/check-conflict?${params.toString()}`, {
        method: 'GET',
        headers: getHeaders(false),
      });

      return handleResponse<BRRConflictCheckResponse>(response);
    },

    /**
     * Delete a BRR review and its session-scoped documents from Neo4j.
     */
    async deleteBRR(batchId: string, sessionId: string): Promise<{ success: boolean; review_deleted: boolean; documents_deleted: number }> {
      const params = new URLSearchParams();
      params.append('batchId', batchId);
      params.append('sessionId', sessionId);

      const response = await authFetch(`${baseUrl}/api/brr/delete?${params.toString()}`, {
        method: 'DELETE',
        headers: getHeaders(false),
      });

      return handleResponse<{ success: boolean; review_deleted: boolean; documents_deleted: number }>(response);
    },

    /**
     * Reassign a BRR review to a new user in the Neo4j graph.
     */
    async reassignReview(payload: {
      batchId: string;
      sessionId: string;
      newUserEmail: string;
    }): Promise<{ success: boolean; neo4jUpdated: boolean }> {
      const response = await authFetch(`${baseUrl}/api/brr/reassign`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(payload),
      });
      return handleResponse<{ success: boolean; neo4jUpdated: boolean }>(response);
    },

    /**
     * Get lightweight metadata for all BRR reviews on a given batch.
     * Used to show existing BRRs that can be cloned as a template.
     */
    async getReviewsByBatch(batchId: string): Promise<BRRReviewsByBatchResponse> {
      const params = new URLSearchParams();
      params.append('batchId', batchId);
      const response = await authFetch(`${baseUrl}/api/brr/reviews-by-batch?${params.toString()}`, {
        method: 'GET',
        headers: getHeaders(false),
      });
      const rawData = await handleResponse<Record<string, unknown>>(response);
      const responseBatchId = String(rawData.batch_id);
      return {
        batchId: rawData.batch_id as number,
        reviews: ((rawData.reviews as Array<Record<string, unknown>>) || []).map((r) => ({
          sessionId: r.session_id as string,
          batchId: responseBatchId,
          userEmail: r.user_email as string,
          status: r.status as 'draft' | 'submitted',
          savedAt: r.saved_at as string | undefined,
          batchType: r.batch_type as 'direct' | 'indirect' | undefined,
          priority: r.priority as 'low' | 'medium' | 'high' | undefined,
          priorityLockedBy: r.priority_locked_by as string | undefined,
          priorityLockedAt: r.priority_locked_at as string | undefined,
          productName: r.product_name as string | undefined,
          inspectionLotId: r.inspection_lot_id as string | undefined,
        })),
        count: rawData.count as number,
      };
    },

    /**
     * Get lightweight metadata for BRR reviews across all batches of a material.
     * Used to find template BRRs from previous batches of the same product.
     */
    async getReviewsByMaterial(materialId: string, limit: number = 10): Promise<BRRReviewsByMaterialResponse> {
      const params = new URLSearchParams();
      params.append('materialId', materialId);
      params.append('limit', String(limit));
      const response = await authFetch(`${baseUrl}/api/brr/reviews-by-material?${params.toString()}`, {
        method: 'GET',
        headers: getHeaders(false),
      });
      const rawData = await handleResponse<Record<string, unknown>>(response);
      return {
        materialId: rawData.material_id as string,
        reviews: ((rawData.reviews as Array<Record<string, unknown>>) || []).map((r) => ({
          sessionId: r.session_id as string,
          batchId: String(r.batch_id),
          userEmail: r.user_email as string,
          status: r.status as 'draft' | 'submitted',
          savedAt: r.saved_at as string | undefined,
          batchType: r.batch_type as 'direct' | 'indirect' | undefined,
          priority: r.priority as 'low' | 'medium' | 'high' | undefined,
          priorityLockedBy: r.priority_locked_by as string | undefined,
          priorityLockedAt: r.priority_locked_at as string | undefined,
          productName: r.product_name as string | undefined,
          inspectionLotId: r.inspection_lot_id as string | undefined,
        })),
        count: rawData.count as number,
      };
    },

    /**
     * Clone an existing BRR review into a new draft session.
     * The source review is never modified — a new BRRReview node is created.
     *
     * Same-batch clone: Full copy including document nodes.
     * Cross-batch clone: Template copy with document placeholders (when targetBatchId differs).
     */
    async cloneBRR(request: BRRCloneRequest): Promise<BRRCloneResponse> {
      const response = await authFetch(`${baseUrl}/api/brr/clone`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(request),
      });
      const rawData = await handleResponse<Record<string, unknown>>(response);
      return {
        batchId: rawData.batch_id as number,
        sourceBatchId: rawData.source_batch_id as string | undefined,
        sourceSessionId: rawData.source_session_id as string,
        newSessionId: rawData.new_session_id as string,
        documentsCloned: rawData.documents_cloned as number,
        documentPlaceholdersCreated: rawData.document_placeholders_created as number | undefined,
        isCrossBatch: rawData.is_cross_batch as boolean | undefined,
        clonedAt: rawData.cloned_at as string,
      };
    },

    /**
     * Get TraceLink lot status for a material/batch combination
     */
    async getTraceLinkLotStatus(materialNumber: string, batchId: string): Promise<TraceLinkLotStatusResponse> {
      const response = await authFetch(`${baseUrl}/api/tracelink/lot-status`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ materialNumber, batchId })
      });
      return handleResponse<TraceLinkLotStatusResponse>(response);
    },

    /**
     * Get TraceLink serial number hierarchy level counts for a material/batch combination
     */
    async getTraceLinkHierarchyLevels(materialNumber: string, batchId: string): Promise<TraceLinkHierarchyLevelsResponse> {
      const response = await authFetch(`${baseUrl}/api/tracelink/serial-hierarchy-levels`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ materialNumber, batchId })
      });
      return handleResponse<TraceLinkHierarchyLevelsResponse>(response);
    },

    /**
     * Fetch a PDF page rendered as a PNG with green highlight rectangles.
     *
     * Returns the image bytes rather than a URL for the caller to put in an
     * `<img src>`: the endpoint requires the bearer token, and an `<img>` cannot
     * send one. Callers turn the blob into an object URL.
     */
    async getDocumentHighlightImage(
      blobName: string,
      page: number,
      rects: FieldHighlightRect[],
      container?: string,
      dpi?: number,
    ): Promise<Blob> {
      const params = new URLSearchParams();
      params.append('blob_name', blobName);
      params.append('page', String(page));
      params.append('rects', JSON.stringify(rects));
      if (container) params.append('container', container);
      if (dpi) params.append('dpi', String(dpi));
      const response = await authFetch(`${baseUrl}/api/documents/highlight-image?${params.toString()}`, {
        method: 'GET',
      });
      if (!response.ok) {
        const errorText = await response.text().catch(() => '');
        throw new Error(`Failed to render document page: ${errorText || response.statusText}`);
      }
      return response.blob();
    },

    async checkCmoQAA(cmoName: string): Promise<QAACheckResponse> {
      const response = await authFetch(`${baseUrl}/api/qaa/check-cmo`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ cmoName })
      });
      return handleResponse<QAACheckResponse>(response);
    },

    async getBoxFolderInfo(folderId: string): Promise<BoxFolderInfo> {
      const response = await authFetch(`${baseUrl}/api/box/folder-info`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ folder_id: folderId })
      });
      return handleResponse<BoxFolderInfo>(response);
    },

    async resolveBoxUrl(url: string): Promise<{ folder_id: string; folder_name: string | null }> {
      const response = await authFetch(`${baseUrl}/api/box/resolve-url`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ url })
      });
      return handleResponse<{ folder_id: string; folder_name: string | null }>(response);
    },

    async scanBoxFolder(
      folderId: string,
      previousSnapshot?: Record<string, BoxFileEntry>,
      timeoutSeconds: number = 120
    ): Promise<BoxFolderScanResult> {
      const controller = new AbortController();
      const clientTimeout = setTimeout(() => controller.abort(), (timeoutSeconds + 10) * 1000);
      try {
        const response = await authFetch(`${baseUrl}/api/box/scan-folder`, {
          method: 'POST',
          headers: getHeaders(),
          signal: controller.signal,
          body: JSON.stringify({
            folder_id: folderId,
            previous_snapshot: previousSnapshot || {},
            timeout_seconds: timeoutSeconds
          })
        });
        return handleResponse<BoxFolderScanResult>(response);
      } finally {
        clearTimeout(clientTimeout);
      }
    },

    async updateMaterialBoxLink(
      materialId: number,
      subsection: BoxSubsectionKey,
      action: 'add' | 'update' | 'delete',
      entry: Partial<BoxLinkEntry> & { id: string }
    ): Promise<{ entries: BoxLinkEntry[] }> {
      const response = await authFetch(`${baseUrl}/api/material/update-box-link`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          material_id: materialId,
          subsection,
          action,
          entry_id: entry.id,
          box_folder_link: entry.link,
          box_folder_id: entry.folder_id,
          snapshot: entry.snapshot,
          version: entry.version,
          free_text: entry.freeText,
        })
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to update material Box link: ${errorText || response.statusText}`);
      }
      return response.json();
    },

    async getDocumentPageCount(blobName: string, container?: string): Promise<number> {
      const params = new URLSearchParams();
      params.append('blob_name', blobName);
      if (container) params.append('container', container);
      const response = await authFetch(`${baseUrl}/api/documents/pdf-page-count?${params.toString()}`, {
        method: 'GET',
        headers: getHeaders(false),
      });
      if (!response.ok) return 1;
      const data = await response.json();
      return data.page_count ?? 1;
    },

    async downloadWordExport(inspectionLotId: string, sessionId?: string, productName?: string, draft?: boolean, brrLevel?: string): Promise<Blob> {
      const params = new URLSearchParams();
      params.append('inspection_lot_id', inspectionLotId);
      if (sessionId) params.append('session_id', sessionId);
      if (productName) params.append('product_name', productName);
      if (draft) params.append('draft', 'true');
      if (brrLevel) params.append('brr_level', brrLevel);
      const response = await authFetch(`${baseUrl}/api/brr/export-word?${params.toString()}`, {
        method: 'GET',
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Word export failed: ${errorText || response.statusText}`);
      }
      return response.blob();
    },

    async exportWordToBox(
      inspectionLotId: string,
      boxFolderId: string,
      sessionId?: string,
      productName?: string,
      draft?: boolean,
      brrLevel?: string,
    ): Promise<{ success: boolean; filename: string; box_folder_id: string; box_file_id?: string }> {
      const response = await authFetch(`${baseUrl}/api/brr/export-word-to-box`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          inspection_lot_id: inspectionLotId,
          box_folder_id: boxFolderId,
          session_id: sessionId,
          product_name: productName,
          draft: draft ?? false,
          brr_level: brrLevel,
        }),
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Word export to Box failed: ${errorText || response.statusText}`);
      }
      return response.json();
    },

    async syncManualSection(
      documentType: string,
      fieldValues: Record<string, string>,
      inspectionLot?: string,
      productName?: string,
    ): Promise<{ status: string; rows: any[] }> {
      const response = await authFetch(`${baseUrl}/api/brr/validation-sync`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          document_type: documentType,
          field_values: fieldValues,
          inspection_lot: inspectionLot,
          product_name: productName,
        }),
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Validation sync failed: ${errorText || response.statusText}`);
      }
      return response.json();
    },

    /**
     * Update priority on an existing BRR review (lightweight PATCH).
     * Admin callers automatically lock the priority.
     */
    async updateReviewPriority(payload: {
      batchId: string;
      sessionId: string;
      priority: 'low' | 'medium' | 'high';
      isAdmin?: boolean;
    }): Promise<{
      batch_id: number;
      session_id: string;
      priority: string;
      priority_locked_by?: string;
      priority_locked_at?: string;
      warnings: string[];
    }> {
      const response = await authFetch(`${baseUrl}/api/brr/review/priority`, {
        method: 'PATCH',
        headers: getHeaders(),
        body: JSON.stringify(payload),
      });
      return handleResponse(response);
    },

    /**
     * Get connection status for all external services (admin diagnostic)
     */
    async getConnectionStatus(): Promise<ConnectionStatusResponse> {
      const response = await authFetch(`${baseUrl}/api/system/connection-status`, {
        method: 'GET',
        headers: getHeaders(false),
      });
      return handleResponse<ConnectionStatusResponse>(response);
    },
  };
};

// Export a type for the API client
export type BRRApiClient = ReturnType<typeof createBRRApi>;
